/** Hosted government notices, requirements, past awards, company fit evidence and response
 *  workspaces for the website. Every call takes a Supabase client so it can be tested. */

export const responseStatusLabel = {
  preparing: 'Preparing',
  ready_to_submit: 'Ready to submit',
  submitted_on_official_portal: 'Submitted on official portal (your declaration)',
  withdrawn: 'Withdrawn',
};
export const fitStatusLabel = { supports: 'Supports', gap: 'Worth checking', unknown: 'Unknown' };

const messages = [
  [/SIGN_IN_REQUIRED/, 'Sign in to continue.'],
  [/FIT_FORBIDDEN|RESPONSE_FORBIDDEN/, "This isn't available to your company account."],
  [/NOTICE_NOT_FOUND/, 'This notice is no longer available.'],
  [/REVISION_CONFLICT/, 'Someone else changed this response. Reload the page, then try again.'],
  [/SUBMISSION_CONFIRMATION_REQUIRED/, 'Confirm that you submitted on the official portal first.'],
  [/CHECKLIST_ITEM_LOCKED/, 'Items from the notice stay on the checklist. Tick them off instead.'],
  [/INVALID_CHECKLIST_ITEM/, 'Enter a checklist item of up to 300 characters.'],
  [/CHECKLIST_FULL/, 'This checklist is full.'],
  [/NOTES_TOO_LONG/, 'Notes can be up to 8,000 characters.'],
  [/REFERENCE_TOO_LONG/, 'The reference can be up to 200 characters.'],
];
function check(error) {
  if (!error) return;
  const match = messages.find(([pattern]) => pattern.test(error.message || ''));
  throw new Error(match ? match[1] : 'Something went wrong. Please try again.');
}
function need(client) { if (!client) throw new Error('Government tender data is not connected on this site yet.'); return client; }
async function call(client, name, args) { const { data, error } = await need(client).rpc(name, args); check(error); return data; }

export const isHostedNoticeId = id => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export const getRecentNotices = (client, limit = 10) => call(client, 'recent_government_notices', { p_limit: limit });
export const getNoticeDetail = (client, id) => call(client, 'government_notice_detail', { p_notice_id: id });
export const getNoticeFit = (client, noticeId, organizationId, refresh = false) =>
  call(client, 'government_notice_fit', { p_notice_id: noticeId, p_organization_id: organizationId, p_refresh: refresh });
export const saveNoticeForResponse = (client, noticeId, organizationId) =>
  call(client, 'save_government_notice', { p_notice_id: noticeId, p_organization_id: organizationId });
export const listResponses = async (client, organizationId) => (await call(client, 'list_government_responses', { p_organization_id: organizationId })) || [];
export const getResponse = (client, id) => call(client, 'get_government_response', { p_response_id: id });

/** Status and notes changes. Can never mark a response as submitted. */
export function updateResponse(client, response, { status = null, notes = null } = {}) {
  if (status === 'submitted_on_official_portal') throw new Error('Use the declaration step to record a submission.');
  if (notes !== null && notes.length > 8000) throw new Error('Notes can be up to 8,000 characters.');
  return call(client, 'update_government_response', { p_response_id: response.id, p_expected_revision: response.revision, p_status: status,
    p_notes: notes, p_submission_reference: null, p_confirm_declared_submission: false });
}
/** The user's own statement that they submitted on the official portal. */
export function declareSubmitted(client, response, reference = '') {
  const clean = String(reference).trim();
  if (clean.length > 200) throw new Error('The reference can be up to 200 characters.');
  return call(client, 'update_government_response', { p_response_id: response.id, p_expected_revision: response.revision,
    p_status: 'submitted_on_official_portal', p_notes: null, p_submission_reference: clean || null, p_confirm_declared_submission: true });
}
export const setChecklistItem = (client, itemId, done) => call(client, 'set_government_checklist_item', { p_item_id: itemId, p_done: Boolean(done) });
export function addChecklistItem(client, responseId, label) {
  const clean = String(label || '').trim();
  if (!clean || clean.length > 300) throw new Error('Enter a checklist item of up to 300 characters.');
  return call(client, 'add_government_checklist_item', { p_response_id: responseId, p_label: clean });
}
export const removeChecklistItem = (client, itemId) => call(client, 'remove_government_checklist_item', { p_item_id: itemId });

export function handoffTarget(notice) {
  return notice?.submissionUrl
    ? { url: notice.submissionUrl, label: 'Go to the official submission portal' }
    : { url: notice?.officialUrl, label: 'Open the official notice to submit' };
}

export async function listSupplierCompanies(client, userId) {
  if (!client || !userId) return [];
  const { data, error } = await client.from('organization_memberships').select('organizations!inner(id,name,kind)')
    .eq('user_id', userId).eq('active', true).eq('organizations.kind', 'supplier');
  check(error);
  return (data || []).flatMap(row => { const org = Array.isArray(row.organizations) ? row.organizations[0] : row.organizations; return org ? [{ id: org.id, name: org.name }] : []; });
}

export function formatLastUpdated(value) {
  if (!value) return 'Update status unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Update status unavailable';
  return `Last updated ${date.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}`;
}
export function formatUkDeadline(value) {
  if (!value) return 'Not published in the source record';
  return `${new Date(value).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })} (UK time)`;
}
export const formatDay = value => (value ? new Date(value).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' }) : 'Date not published');
export function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return 'Value not published';
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP', maximumFractionDigits: 0 }).format(Number(amount)); }
  catch { return `${currency} ${Math.round(Number(amount)).toLocaleString('en-GB')}`; }
}
